import React from 'react';
import { Box } from '@mui/material';
import { LEVEL_COLORS } from './JsonTree/constants';

const textEncoder = new TextEncoder();

export const isObjectLike = (v) => v !== null && typeof v === 'object';

export const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const stringForMatch = (v) => (v === null ? 'null' : v === undefined ? 'undefined' : typeof v === 'string' ? v : String(v));

export const formatPrimitive = (v) => (v === null ? 'null' : v === undefined ? 'undefined' : typeof v === 'string' ? `"${v}"` : String(v));

export const parseInput = (original, text) => {
  const t = text.trim();
  if (t.toLowerCase() === 'null') return null;
  if (t.toLowerCase() === 'undefined') return undefined;
  if (t.toLowerCase() === 'true') return true;
  if (t.toLowerCase() === 'false') return false;

  if (typeof original === 'number') {
    const n = Number(t);
    return Number.isNaN(n) ? original : n;
  }
  if (typeof original === 'boolean') return t.toLowerCase() === 'true';
  if (typeof original === 'string') return t;

  const n = Number(t);
  return Number.isNaN(n) ? t : n;
};

export const subtreeHasMatch = (label, value, normalizedSearch, exactMatch = false) => {
  if (!normalizedSearch) return true;

  const labelText = typeof label === 'string' ? label.toLowerCase() : '';
  const labelMatches = labelText ? (exactMatch ? labelText === normalizedSearch : labelText.includes(normalizedSearch)) : false;

  if (!isObjectLike(value)) {
    const valueText = stringForMatch(value).toLowerCase();
    return labelMatches || (exactMatch ? valueText === normalizedSearch : valueText.includes(normalizedSearch));
  }

  const entries = Array.isArray(value) ? value.map((item, idx) => [`[${idx}]`, item]) : Object.entries(value);

  return labelMatches || entries.some(([k, v]) => subtreeHasMatch(k, v, normalizedSearch, exactMatch));
};

export const highlightText = (text, term) => {
  if (!term) return text;
  const regex = new RegExp(`(${escapeRegExp(term)})`, 'ig');
  return String(text)
    .split(regex)
    .map((part, i) =>
      part.toLowerCase() === term.toLowerCase() ? (
        <Box key={i} component="mark" sx={{ px: 0.25, borderRadius: 0.5, backgroundColor: (theme) => theme.palette.warning.light, color: 'inherit' }}>
          {part}
        </Box>
      ) : (
        <React.Fragment key={i}>{part}</React.Fragment>
      )
    );
};

export const getLevelBackground = (level) => LEVEL_COLORS[level % LEVEL_COLORS.length];

export const getTopLevelEntrySizeBytes = (key, value) => {
  const serializedValue = JSON.stringify(value);
  if (serializedValue != undefined) {
    const serializedEntry = `${JSON.stringify(String(key))}:${serializedValue}`;
    return textEncoder.encode(serializedEntry).length;
  }
};

export const setIn = (obj, path, value) => {
  if (path.length === 0) return value;
  const [head, ...rest] = path;
  if (Array.isArray(obj)) {
    const idx = head;
    const next = obj?.[idx];
    const copy = obj.slice();
    copy[idx] = setIn(next, rest, value);
    return copy;
  }
  const next = obj?.[head];
  return { ...(obj || {}), [head]: setIn(next, rest, value) };
};

export const getIn = (obj, path) => {
  if (!path || path.length === 0) return obj;
  return path.reduce((acc, key) => {
    if (acc === null || acc === undefined) return acc;
    return acc[key];
  }, obj);
};
